import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';

import { useNotification } from './Notification';
import { theme } from '../theme';
import { GlobalStatsResponse, PopularBadge } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';

interface GlobalStatsProps {
  isVisible?: boolean;
  onClose?: () => void;
}

export const GlobalStats: React.FC<GlobalStatsProps> = ({
  isVisible = true,
  onClose
}) => {
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [stats, setStats] = useState<GlobalStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isVisible) {
      fetchGlobalStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const fetchGlobalStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/gamification/global-stats'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando estadísticas', 'error');
      }
    } catch (error) {
      logger.error('Error fetching global stats:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatCardStyle = (color: string) => ({
    background: `linear-gradient(135deg, ${color}20, ${color}40)`,
    border: `2px solid ${color}`,
    boxShadow: `0 4px 15px ${color}40`,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.card,
    textAlign: 'center' as const,
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  });

  const getBadgeStyle = (badge: PopularBadge) => ({
    background: `linear-gradient(135deg, ${badge.color}20, ${badge.color}40)`,
    border: `2px solid ${badge.color}`,
    boxShadow: `0 4px 15px ${badge.color}40`,
    position: 'relative' as const,
    overflow: 'hidden'
  });

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '1000px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
          <h2 style={{
            margin: 0,
            color: '#FFD700',
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle,
            textShadow: '0 0 10px #FFD700',
            background: 'linear-gradient(45deg, #FFD700, #FFA500)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            📊 Estadísticas Globales
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{
              fontSize: '24px',
              animation: 'spin 1s linear infinite'
            }}>
              ⚽
            </div>
            <p style={{ color: theme.colors.textSecondary }}>Cargando estadísticas...</p>
          </div>
        ) : stats ? (
          <div>
            {/* Estadísticas principales */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: theme.spacing.md,
              marginBottom: theme.spacing.lg
            }}>
              <div
                style={getStatCardStyle('#4CAF50')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>👥</div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#4CAF50',
                  marginBottom: '4px'
                }}>
                  {stats.globalStats.totalUsers.toLocaleString()}
                </div>
                <div style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  Usuarios Registrados
                </div>
              </div>

              <div
                style={getStatCardStyle('#2196F3')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⭐</div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#2196F3',
                  marginBottom: '4px'
                }}>
                  {stats.globalStats.totalReviews.toLocaleString()}
                </div>
                <div style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  Reviews Totales
                </div>
              </div>

              <div
                style={getStatCardStyle('#FF9800')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏆</div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#FF9800',
                  marginBottom: '4px'
                }}>
                  {stats.globalStats.totalBadgesEarned.toLocaleString()}
                </div>
                <div style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  Badges Otorgados
                </div>
              </div>

              <div
                style={getStatCardStyle('#9C27B0')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📈</div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#9C27B0',
                  marginBottom: '4px'
                }}>
                  {stats.globalStats.positivePercentage}
                </div>
                <div style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  Reviews Positivas
                </div>
              </div>
            </div>

            {/* Estadísticas detalladas */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: theme.spacing.lg,
              marginBottom: theme.spacing.lg
            }}>
              {/* Etiquetas */}
              <div style={{
                background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
                borderRadius: theme.borderRadius.card,
                padding: theme.spacing.md,
                border: '1px solid #333'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  color: '#FFD700',
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeBody,
                  textAlign: 'center'
                }}>
                  📊 Etiquetas Utilizadas
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: theme.spacing.md
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#4CAF50',
                      marginBottom: '4px'
                    }}>
                      {stats.globalStats.totalPositiveTags.toLocaleString()}
                    </div>
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      Positivas
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#F44336',
                      marginBottom: '4px'
                    }}>
                      {stats.globalStats.totalNegativeTags.toLocaleString()}
                    </div>
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      Negativas
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#FF9800',
                      marginBottom: '4px'
                    }}>
                      {stats.globalStats.totalCustomTags.toLocaleString()}
                    </div>
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      Personalizadas
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#2196F3',
                      marginBottom: '4px'
                    }}>
                      {stats.globalStats.averageReviewsPerUser}
                    </div>
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      Promedio por Usuario
                    </div>
                  </div>
                </div>
              </div>

              {/* Promedios */}
              <div style={{
                background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
                borderRadius: theme.borderRadius.card,
                padding: theme.spacing.md,
                border: '1px solid #333'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  color: '#FFD700',
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeBody,
                  textAlign: 'center'
                }}>
                  📈 Promedios
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.sm
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #333'
                  }}>
                    <span style={{ color: theme.colors.textSecondary }}>Reviews por Usuario:</span>
                    <span style={{ fontWeight: 'bold', color: theme.colors.text }}>
                      {stats.globalStats.averageReviewsPerUser}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #333'
                  }}>
                    <span style={{ color: theme.colors.textSecondary }}>Badges por Usuario:</span>
                    <span style={{ fontWeight: 'bold', color: theme.colors.text }}>
                      {(stats.globalStats.totalBadgesEarned / stats.globalStats.totalUsers).toFixed(1)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #333'
                  }}>
                    <span style={{ color: theme.colors.textSecondary }}>Etiquetas por Review:</span>
                    <span style={{ fontWeight: 'bold', color: theme.colors.text }}>
                      {((stats.globalStats.totalPositiveTags + stats.globalStats.totalNegativeTags + stats.globalStats.totalCustomTags) / stats.globalStats.totalReviews).toFixed(1)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{ color: theme.colors.textSecondary }}>Participación:</span>
                    <span style={{ fontWeight: 'bold', color: theme.colors.text }}>
                      {((stats.globalStats.totalReviews / stats.globalStats.totalUsers) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Badges más populares */}
            <div style={{
              background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
              borderRadius: theme.borderRadius.card,
              padding: theme.spacing.md,
              border: '1px solid #333',
              marginBottom: theme.spacing.lg
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                color: '#FFD700',
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                textAlign: 'center'
              }}>
                🏆 Badges Más Populares
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: theme.spacing.md
              }}>
                {stats.popularBadges.slice(0, 6).map((badge) => (
                  <div
                    key={badge.id}
                    style={{
                      padding: theme.spacing.md,
                      borderRadius: theme.borderRadius.card,
                      ...getBadgeStyle(badge),
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: 'scale(1)',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.md,
                      marginBottom: theme.spacing.sm
                    }}>
                      <div style={{
                        fontSize: '24px',
                        filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.3))'
                      }}>
                        {badge.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 4px 0',
                          color: badge.color,
                          fontFamily: theme.fonts.body,
                          fontSize: theme.fonts.sizeBody,
                          fontWeight: theme.fonts.weightBold,
                          textShadow: `0 0 5px ${badge.color}`
                        }}>
                          {badge.name}
                        </h4>
                        <p style={{
                          margin: 0,
                          color: theme.colors.textSecondary,
                          fontSize: theme.fonts.sizeSmall,
                          lineHeight: '1.4'
                        }}>
                          {badge.description}
                        </p>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: theme.spacing.sm,
                      paddingTop: theme.spacing.sm,
                      borderTop: `1px solid ${badge.color}40`
                    }}>
                      <span style={{
                        color: badge.color,
                        fontSize: theme.fonts.sizeSmall,
                        fontWeight: theme.fonts.weightBold
                      }}>
                        {badge.requirement}
                      </span>
                      <span style={{
                        color: '#FFD700',
                        fontSize: theme.fonts.sizeSmall,
                        fontWeight: 'bold'
                      }}>
                        {badge.earnedCount} obtenidos
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: theme.spacing.lg,
            color: theme.colors.textSecondary
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <h3 style={{ color: theme.colors.text, marginBottom: '8px' }}>
              No se pudieron cargar las estadísticas
            </h3>
            <p style={{ color: theme.colors.textSecondary }}>
              Intenta nuevamente más tarde
            </p>
          </div>
        )}

        {/* Botón cerrar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing.lg
        }}>
          <button
            onClick={onClose}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: '#000',
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: theme.fonts.weightBold,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
            }}
          >
            Cerrar
          </button>
        </div>

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}; 